import axios from '../lib/axios';
import { AxiosError } from 'axios';
import { tokenStorage } from '../utils/tokenStorage';
import { logger } from '../utils/logger';

// API Endpoints
const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface PointHistoryItem {
  _id: string;
  clientId: string;
  membershipId: string;
  userId: string;
  pointsAmount: number;
  transactionType: 'earned' | 'redeemed' | 'expired' | 'bonus';
  description: string;
  balanceBefore: number;
  balanceAfter: number;
  transactionId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface ProfileHistoryResponse {
  success: boolean;
  message?: string;
  data: {
    history: PointHistoryItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

// Keep backward compatibility interfaces
export interface LegacyPointHistoryItem extends PointHistoryItem {
  type: 'earned' | 'redeemed' | 'expired' | 'bonus';
  points: number;
  rewardedPoints: number;
  orderNumber: string;
  status: 'active' | 'completed' | 'expired';
  orderId?: string;
  expiresAt?: string;
}

export interface PointHistoryResponse {
  success: boolean;
  message?: string;
  data: {
    pointHistory: LegacyPointHistoryItem[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface PointSummary {
  totalPointsEarned: number;
  totalPointsRedeemed: number;
  totalPointsExpired: number;
  currentBalance: number;
  pendingPoints: number;
  pointsExpiringIn30Days: number;
  lastEarnedDate?: string;
  lastRedeemedDate?: string;
}

export interface PointSummaryResponse {
  success: boolean;
  message?: string;
  data: PointSummary;
}

// Helper function to get headers with authentication
const getHeaders = async () => {
  const accessToken = tokenStorage.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
};

export const loyaltyService = {
  /**
   * Get profile history for a user (new single API endpoint)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   */
  getProfileHistory: async (
    page: number = 1,
    limit: number = 20,
  ): Promise<ProfileHistoryResponse> => {
    try {
      logger.log('Fetching profile history');
      const headers = await getHeaders();

      const url = `${BASE_URL}/api/v1/users/profile-history`;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      // Single-flight + cache per parameter key
      const key = `profile-history|${page}|${limit}`;

      // Use cache if fresh
      const cached = historyCache.get(key);
      if (cached && Date.now() - cached.fetchedAt < HISTORY_CACHE_TTL) {
        return cached.response as ProfileHistoryResponse;
      }

      // Respect failure cooldown
      if (lastHistoryErrorTime && Date.now() - lastHistoryErrorTime < HISTORY_FAILURE_COOLDOWN_MS) {
        const err = new Error('Profile history fetch paused due to recent errors');
        (err as Error & { isCooldown?: boolean }).isCooldown = true;
        throw err;
      }

      // Reuse in-flight request if present
      const existingPromise = historyInFlight.get(key);
      if (existingPromise) {
        return (await existingPromise) as ProfileHistoryResponse;
      }

      const requestPromise = axios
        .get(`${url}?${params.toString()}`, { headers })
        .then((response) => {
          logger.log('Profile history API response:', response.data);
          if (response.data?.success) {
            const data = response.data as ProfileHistoryResponse;
            historyCache.set(key, { response: data, fetchedAt: Date.now() });
            return data;
          }
          throw new Error(response.data?.message || 'Failed to fetch profile history');
        })
        .catch((error) => {
          lastHistoryErrorTime = Date.now();
          throw error;
        })
        .finally(() => {
          historyInFlight.delete(key);
        });

      historyInFlight.set(key, requestPromise);
      return await requestPromise;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Failed to fetch profile history:', axiosError);

      throw new Error(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          axiosError.message ||
          'Failed to fetch profile history',
      );
    }
  },

  /**
   * Get point history for a user (legacy format for backward compatibility)
   * @param userId - User ID
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   */
  getPointHistory: async (page: number = 1, limit: number = 10): Promise<PointHistoryResponse> => {
    try {
      // Use the new profile history API
      const profileResponse = await loyaltyService.getProfileHistory(page, limit);

      // Transform to old format for backward compatibility
      const transformedResponse: PointHistoryResponse = {
        success: profileResponse.success,
        message: profileResponse.message,
        data: {
          pointHistory: profileResponse.data.history.map(
            (item) =>
              ({
                ...item,
                type: item.transactionType,
                points: item.pointsAmount,
                rewardedPoints: item.pointsAmount,
                // Safely derive order number from description or fallback to TXN-suffix
                orderNumber: (() => {
                  const descriptionStr = item.description ?? '';
                  const extractedOrder = descriptionStr.match(/order\s+([A-Za-z0-9_-]+)/i)?.[1];
                  const txnSuffix =
                    item.transactionId?.slice(-8) ?? item._id?.slice(-8) ?? 'UNKNOWN';
                  return extractedOrder ?? `TXN-${txnSuffix}`;
                })(),
                status: 'active' as const,
                orderId: item.transactionId ?? item._id,
                expiresAt: undefined,
              }) as LegacyPointHistoryItem,
          ),
          pagination: {
            currentPage: profileResponse.data.pagination.page,
            totalPages: profileResponse.data.pagination.totalPages,
            totalItems: profileResponse.data.pagination.total,
            itemsPerPage: profileResponse.data.pagination.limit,
            hasNextPage: profileResponse.data.pagination.hasNext,
            hasPrevPage: profileResponse.data.pagination.hasPrev,
          },
        },
      };

      return transformedResponse;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Failed to fetch point history:', axiosError);

      throw new Error(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          axiosError.message ||
          'Failed to fetch point history',
      );
    }
  },

  /**
   * Get point summary for a user (calculated from history)
   * @param userId - User ID
   */
  getPointSummary: async (userId: string): Promise<PointSummaryResponse> => {
    try {
      logger.log('Calculating point summary for user:', userId);

      // Get all history to calculate summary
      const profileResponse = await loyaltyService.getProfileHistory(1, 1000); // Get more records for accurate summary

      if (!profileResponse.success || !profileResponse.data.history) {
        throw new Error('Failed to fetch history for summary calculation');
      }

      const history = profileResponse.data.history;

      // Calculate summary from history
      let totalPointsEarned = 0;
      let totalPointsRedeemed = 0;
      let totalPointsExpired = 0;
      let lastEarnedDate: string | undefined;
      let lastRedeemedDate: string | undefined;

      history.forEach((item) => {
        switch (item.transactionType) {
          case 'earned':
          case 'bonus':
            totalPointsEarned += item.pointsAmount;
            if (!lastEarnedDate || new Date(item.createdAt) > new Date(lastEarnedDate)) {
              lastEarnedDate = item.createdAt;
            }
            break;
          case 'redeemed':
            totalPointsRedeemed += item.pointsAmount;
            if (!lastRedeemedDate || new Date(item.createdAt) > new Date(lastRedeemedDate)) {
              lastRedeemedDate = item.createdAt;
            }
            break;
          case 'expired':
            totalPointsExpired += item.pointsAmount;
            break;
        }
      });

      // Current balance should be the latest balanceAfter from the most recent transaction
      const currentBalance =
        history.length > 0
          ? history.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            )[0].balanceAfter
          : 0;

      const summary: PointSummary = {
        totalPointsEarned,
        totalPointsRedeemed,
        totalPointsExpired,
        currentBalance,
        pendingPoints: 0, // Not available in new API
        pointsExpiringIn30Days: 0, // Not available in new API
        lastEarnedDate,
        lastRedeemedDate,
      };

      return {
        success: true,
        data: summary,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Failed to calculate point summary:', axiosError);

      throw new Error(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          axiosError.message ||
          'Failed to calculate point summary',
      );
    }
  },

  /**
   * Transform point history item for backward compatibility
   * Maps new API response to old PointHistoryItem format
   */
  transformPointHistoryItem: (item: PointHistoryItem): FormattedPointHistoryItem => {
    // Handle the new API structure
    const points = item.pointsAmount;
    const createdAt = item.createdAt;

    // Extract date and time components for potential separate fields
    const dateObj = new Date(createdAt);
    const date = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
    const time = dateObj.toTimeString().split(' ')[0]; // HH:MM:SS format

    // Extract order number from description if available
    const descriptionStr = item.description ?? '';
    const txnSuffix = item.transactionId?.slice(-8) ?? item._id?.slice(-8) ?? 'UNKNOWN';
    const extractedOrder = descriptionStr.match(/order\s+([A-Za-z0-9_-]+)/i)?.[1];
    const orderNumber = extractedOrder ? extractedOrder : `TXN-${txnSuffix}`;

    return {
      _id: item._id,
      orderNumber,
      rewardedPoints: points,
      points: points,
      createdAt: createdAt,
      date: date,
      time: time,
      totalAmount: 0, // Not available in new API
      status: 'completed',
      type: item.transactionType,
      description: item.description,
      userId: item.userId,
      orderId: item.transactionId ?? item._id,
      expiresAt: undefined,
      metadata: item.metadata,
    };
  },

  /**
   * Get formatted point history for a user
   * This maintains compatibility with existing UI components
   */
  getFormattedPointHistory: async (
    page: number = 1,
    limit: number = 10,
  ): Promise<FormattedPointHistoryItem[]> => {
    try {
      const profileResponse = await loyaltyService.getProfileHistory(page, limit);

      if (profileResponse.success && profileResponse.data?.history) {
        return profileResponse.data.history.map(loyaltyService.transformPointHistoryItem);
      }

      return [];
    } catch (error) {
      logger.error('Failed to get formatted point history:', error);
      return [];
    }
  },

  /**
   * Get formatted point history with pagination for display
   * This provides both formatted items and pagination info
   */
  getFormattedPointHistoryWithPagination: async (
    page: number = 1,
    limit: number = 10,
  ): Promise<{
    items: FormattedPointHistoryItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> => {
    try {
      const profileResponse = await loyaltyService.getProfileHistory(page, limit);

      if (profileResponse.success && profileResponse.data?.history) {
        return {
          items: profileResponse.data.history.map(loyaltyService.transformPointHistoryItem),
          pagination: profileResponse.data.pagination,
        };
      }

      return {
        items: [],
        pagination: {
          page: 1,
          limit: limit,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    } catch (error) {
      logger.error('Failed to get formatted point history with pagination:', error);
      return {
        items: [],
        pagination: {
          page: 1,
          limit: limit,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  },
};

export default loyaltyService;

// Caching and single-flight controls for history
const HISTORY_CACHE_TTL = 10_000; // 10s
const HISTORY_FAILURE_COOLDOWN_MS = 10_000;

const historyCache = new Map<string, { response: ProfileHistoryResponse; fetchedAt: number }>();
const historyInFlight = new Map<string, Promise<ProfileHistoryResponse>>();
let lastHistoryErrorTime = 0;

export interface FormattedPointHistoryItem {
  _id: string;
  orderNumber: string;
  rewardedPoints: number;
  points: number;
  createdAt: string;
  date: string;
  time: string;
  totalAmount: number;
  status: string;
  type: PointHistoryItem['transactionType'];
  description?: string;
  userId: string;
  orderId?: string;
  expiresAt?: string;
  metadata?: PointHistoryItem['metadata'];
}
